export class AudioManager {
    constructor() {
        this.audioContext = null;
        this.musicEnabled = true;
        this.effectsEnabled = true;
        this.masterVolume = 0.5;

        // OGG BGM element
        this.musicEl = null;
        this.musicPlaying = false;
        this.musicUrl = '/audio/space-ambient.ogg';

        // Fallback oscillator (if OGG fails)
        this.musicOscillator = null;
        this.musicGain = null;
        this._vib = null;

        // Load settings from localStorage
        this.loadSettings();

        // Initialize on user interaction
        this.initialized = false;
    }

    init() {
        if (this.initialized) return;

        try {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            this.initialized = true;

            if (this.musicEnabled) {
                this.startMusic();
            }
        } catch (error) {
            console.warn('Web Audio API not supported:', error);
        }
    }

    loadSettings() {
        const saved = localStorage.getItem('audioSettings');
        if (saved) {
            const settings = JSON.parse(saved);
            this.musicEnabled = settings.musicEnabled !== false;
            this.effectsEnabled = settings.effectsEnabled !== false;
        }
    }

    saveSettings() {
        localStorage.setItem('audioSettings', JSON.stringify({
            musicEnabled: this.musicEnabled,
            effectsEnabled: this.effectsEnabled
        }));
    }

    setMusicEnabled(enabled) {
        this.musicEnabled = enabled;
        this.saveSettings();

        if (!this.initialized) return;

        if (enabled) {
            this.startMusic();
        } else {
            this.stopMusic();
        }
    }

    setEffectsEnabled(enabled) {
        this.effectsEnabled = enabled;
        this.saveSettings();
    }

    startMusic() {
        if (!this.initialized || !this.musicEnabled || this.musicPlaying) return;

        try {
            // Prefer looping OGG ambient track
            if (!this.musicEl) {
                this.musicEl = new Audio(this.musicUrl);
                this.musicEl.loop = true;
                this.musicEl.preload = 'auto';
                this.musicEl.volume = Math.min(1, 0.32 * this.masterVolume);
            }
            const play = this.musicEl.play();
            if (play && typeof play.then === 'function') {
                play.then(() => {
                    this.musicPlaying = true;
                }).catch(() => {
                    // Autoplay / decode failure → procedural pad
                    this._startFallbackPad();
                });
            } else {
                this.musicPlaying = true;
            }
        } catch (error) {
            console.warn('Failed to start OGG music, using pad:', error);
            this._startFallbackPad();
        }
    }

    _startFallbackPad() {
        if (!this.audioContext || this.musicPlaying) return;
        try {
            this.musicGain = this.audioContext.createGain();
            this.musicGain.gain.value = 0.12 * this.masterVolume;
            this.musicGain.connect(this.audioContext.destination);

            this.musicOscillator = this.audioContext.createOscillator();
            this.musicOscillator.type = 'sine';
            this.musicOscillator.frequency.value = 110;

            this._vib = this.audioContext.createOscillator();
            this._vib.frequency.value = 0.15;
            const vg = this.audioContext.createGain();
            vg.gain.value = 8;
            this._vib.connect(vg);
            vg.connect(this.musicOscillator.frequency);

            this.musicOscillator.connect(this.musicGain);
            this.musicOscillator.start();
            this._vib.start();
            this.musicPlaying = true;
        } catch (e) {
            console.warn('Fallback music failed:', e);
        }
    }

    stopMusic() {
        if (!this.musicPlaying && !this.musicEl) return;

        try {
            if (this.musicEl) {
                this.musicEl.pause();
                this.musicEl.currentTime = 0;
            }
            if (this.musicOscillator) {
                try { this.musicOscillator.stop(); } catch (_) {}
                this.musicOscillator.disconnect();
                this.musicOscillator = null;
            }
            if (this._vib) {
                try { this._vib.stop(); } catch (_) {}
                this._vib.disconnect();
                this._vib = null;
            }
            if (this.musicGain) {
                this.musicGain.disconnect();
                this.musicGain = null;
            }
            this.musicPlaying = false;
        } catch (error) {
            console.warn('Failed to stop music:', error);
        }
    }

    // Sound effect generators
    playShoot() {
        if (!this.initialized || !this.effectsEnabled) return;

        try {
            const osc = this.audioContext.createOscillator();
            const gain = this.audioContext.createGain();

            osc.type = 'square';
            osc.frequency.value = 440;
            osc.frequency.exponentialRampToValueAtTime(110, this.audioContext.currentTime + 0.1);

            gain.gain.value = 0.1 * this.masterVolume;
            gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.1);

            osc.connect(gain);
            gain.connect(this.audioContext.destination);

            osc.start();
            osc.stop(this.audioContext.currentTime + 0.1);
        } catch (error) {
            console.warn('Failed to play shoot sound:', error);
        }
    }

    playHit() {
        if (!this.initialized || !this.effectsEnabled) return;

        try {
            const osc = this.audioContext.createOscillator();
            const gain = this.audioContext.createGain();

            osc.type = 'sawtooth';
            osc.frequency.value = 100;

            gain.gain.value = 0.15 * this.masterVolume;
            gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.15);

            osc.connect(gain);
            gain.connect(this.audioContext.destination);

            osc.start();
            osc.stop(this.audioContext.currentTime + 0.15);
        } catch (error) {
            console.warn('Failed to play hit sound:', error);
        }
    }

    playCoin() {
        if (!this.initialized || !this.effectsEnabled) return;

        try {
            const osc = this.audioContext.createOscillator();
            const gain = this.audioContext.createGain();

            osc.type = 'sine';
            osc.frequency.value = 800;
            osc.frequency.exponentialRampToValueAtTime(1200, this.audioContext.currentTime + 0.05);

            gain.gain.value = 0.12 * this.masterVolume;
            gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.15);

            osc.connect(gain);
            gain.connect(this.audioContext.destination);

            osc.start();
            osc.stop(this.audioContext.currentTime + 0.15);
        } catch (error) {
            console.warn('Failed to play coin sound:', error);
        }
    }

    playLevelUp() {
        if (!this.initialized || !this.effectsEnabled) return;

        try {
            const playNote = (freq, startTime, duration) => {
                const osc = this.audioContext.createOscillator();
                const gain = this.audioContext.createGain();

                osc.type = 'sine';
                osc.frequency.value = freq;

                gain.gain.value = 0.15 * this.masterVolume;
                gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + startTime + duration);

                osc.connect(gain);
                gain.connect(this.audioContext.destination);

                osc.start(this.audioContext.currentTime + startTime);
                osc.stop(this.audioContext.currentTime + startTime + duration);
            };

            // Play a chord
            playNote(523, 0, 0.3);      // C5
            playNote(659, 0.1, 0.3);    // E5
            playNote(784, 0.2, 0.4);    // G5
        } catch (error) {
            console.warn('Failed to play level up sound:', error);
        }
    }

    playPowerUp() {
        if (!this.initialized || !this.effectsEnabled) return;

        try {
            const osc = this.audioContext.createOscillator();
            const gain = this.audioContext.createGain();

            osc.type = 'sine';
            osc.frequency.value = 400;
            osc.frequency.exponentialRampToValueAtTime(1600, this.audioContext.currentTime + 0.2);

            gain.gain.value = 0.15 * this.masterVolume;
            gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.25);

            osc.connect(gain);
            gain.connect(this.audioContext.destination);

            osc.start();
            osc.stop(this.audioContext.currentTime + 0.25);
        } catch (error) {
            console.warn('Failed to play power-up sound:', error);
        }
    }

    playDeath() {
        if (!this.initialized || !this.effectsEnabled) return;

        try {
            const osc = this.audioContext.createOscillator();
            const gain = this.audioContext.createGain();

            osc.type = 'sawtooth';
            osc.frequency.value = 200;
            osc.frequency.exponentialRampToValueAtTime(50, this.audioContext.currentTime + 0.5);

            gain.gain.value = 0.2 * this.masterVolume;
            gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.5);

            osc.connect(gain);
            gain.connect(this.audioContext.destination);

            osc.start();
            osc.stop(this.audioContext.currentTime + 0.5);
        } catch (error) {
            console.warn('Failed to play death sound:', error);
        }
    }

    playExplosion() {
        if (!this.initialized || !this.effectsEnabled) return;

        try {
            // Create noise for explosion effect
            const bufferSize = this.audioContext.sampleRate * 0.3;
            const buffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate);
            const data = buffer.getChannelData(0);

            for (let i = 0; i < bufferSize; i++) {
                data[i] = Math.random() * 2 - 1;
            }

            const noise = this.audioContext.createBufferSource();
            noise.buffer = buffer;

            const gain = this.audioContext.createGain();
            gain.gain.value = 0.2 * this.masterVolume;
            gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3);

            const filter = this.audioContext.createBiquadFilter();
            filter.type = 'lowpass';
            filter.frequency.value = 1000;

            noise.connect(filter);
            filter.connect(gain);
            gain.connect(this.audioContext.destination);

            noise.start();
            noise.stop(this.audioContext.currentTime + 0.3);
        } catch (error) {
            console.warn('Failed to play explosion sound:', error);
        }
    }

    playPortal() {
        if (!this.initialized || !this.effectsEnabled) return;

        try {
            const osc = this.audioContext.createOscillator();
            const gain = this.audioContext.createGain();

            osc.type = 'sine';
            osc.frequency.value = 300;
            osc.frequency.exponentialRampToValueAtTime(150, this.audioContext.currentTime + 0.3);

            gain.gain.value = 0.1 * this.masterVolume;
            gain.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3);

            osc.connect(gain);
            gain.connect(this.audioContext.destination);

            osc.start();
            osc.stop(this.audioContext.currentTime + 0.3);
        } catch (error) {
            console.warn('Failed to play portal sound:', error);
        }
    }

    // Clean up
    destroy() {
        this.stopMusic();
        if (this.audioContext) {
            this.audioContext.close();
        }
    }
}
